"use client";

import { Archive, Bot, Cpu, HardDrive, Server, type LucideIcon } from "lucide-react";

import { DATA_LABEL_CLS, DISPLAY } from "@/components/editorial";
import { UkMark } from "@/components/editorial/marks";

import { FrameContents } from "@/components/product-shot/frame-contents";
import styles from "./landing-uk-story.module.css";
import { UkDotMap } from "./uk-dot-map";

/*
 * Why a fully UK-hosted Twin matters, as a band on the home: one
 * panel, the whole stack inside the UK's coastline with the outside
 * provider left at sea, beside the four things that changes for the reader.
 * It describes the fully UK-hosted Twin, in development (its model, Project
 * Venus, already serves from dedicated UK or EU infrastructure); the
 * words stay inside the site's own: model processing can happen elsewhere
 * with an outside provider (the trust band), and a private model on UK GPUs
 * keeps prompts and business context inside the boundary (/research).
 */

const INK_72 = "text-[color-mix(in_srgb,var(--theme-ink)_72%,transparent)]";
const INK_88 = "text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)]";

/** The site's editorial look, or the application's own design system (LAB-007 · One system). */
type Look = "editorial" | "app";

/** The stack a question runs through, all of it inside the line. */
const STACK: readonly { icon: LucideIcon; label: string; where: string }[] = [
  { icon: Server, label: "Server", where: "UK datacentre" },
  { icon: HardDrive, label: "Storage", where: "UK datacentre" },
  { icon: Cpu, label: "Private model", where: "UK GPUs" },
  { icon: Archive, label: "Logs and backups", where: "In the UK" },
];

const BENEFITS: readonly { title: string; body: string }[] = [
  {
    title: "Say yes to sensitive work",
    body: "Client files, contracts and personal data stay inside the line your policies draw, so AI can reach the work it was kept away from.",
  },
  { title: "No outside model provider", body: "Prompts and business context go to a private model on UK GPUs, not to a third-party AI service." },
  { title: "One boundary to review", body: "Server, storage, model, logs and backups sit in one place, so your security review has one set of answers." },
  { title: "The same checked answers", body: "Sources, definitions and checks work as they do today, whatever model answers." },
];

/** The boundary panel: the UK, the stack on its spine inside it, and the outside provider at sea. */
function BoundaryPanel() {
  return (
    // A panel on the page, as a product shot's window is: it keeps the corners they share (one-system's --frame-radius).
    <figure data-frame-corners="" className={`${styles.panel} relative m-0 overflow-hidden rounded-[var(--frame-radius,8px)] px-6 pt-6 pb-5 sm:px-8 sm:pt-8`}>
      <div className="flex items-center justify-between text-[0.8rem]">
        <span className="flex items-center gap-2 font-medium">
          <UkMark /> United Kingdom
        </span>
        <span className="text-[rgb(246_239_232/0.6)]">In development</span>
      </div>
      <div className="relative mx-auto mt-4 aspect-[592/935] w-full max-w-[21rem]">
        {/* The map's 1,100 dots are drawn in the browser, as the panel
            arrives: the page's HTML does not carry them, and the sweep from
            the south coast plays where it can be seen (it ran at page load,
            out of sight). The box keeps the map's shape meanwhile. */}
        <FrameContents ahead="0px">
          <UkDotMap className="absolute inset-0 h-full w-full text-[rgb(246_239_232/0.34)]" sweepMs={1300} />
        </FrameContents>
        {/* The stack, on a spine over England, a packet running it. */}
        <div className="absolute top-[70%] left-[66%] -translate-x-1/2 -translate-y-1/2">
          <div className="relative flex flex-col items-stretch gap-2.5 py-1">
            <span aria-hidden className={styles.spine} />
            <span aria-hidden className={`${styles.packet} rounded-full`} />
            {STACK.map((part) => (
              <p key={part.label} className={`${styles.chip} relative flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[12.5px] whitespace-nowrap`}>
                <part.icon aria-hidden className="size-3.5 shrink-0 opacity-80" />
                <span className="font-medium">{part.label}</span>
                <span className="text-[rgb(246_239_232/0.72)]">{part.where}</span>
              </p>
            ))}
          </div>
        </div>
      </div>
      {/* The outside provider, out at sea off the east coast, with no route to it. */}
      <div className="absolute top-[34%] right-5 flex flex-col items-end gap-1 opacity-70 sm:right-7">
        <p className={`${styles.chip} flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[12.5px] whitespace-nowrap`}>
          <Bot aria-hidden className="size-3.5" /> Outside AI provider
        </p>
        <p className="text-[11px] text-[rgb(246_239_232/0.7)]">Not on the route</p>
      </div>
      <figcaption className="mt-4 border-t border-[rgb(246_239_232/0.14)] pt-3 text-[0.82rem] text-[rgb(246_239_232/0.72)]">
        Everything on the route, from the question to the answer, runs inside the UK.
      </figcaption>
    </figure>
  );
}

/**
 * Why keep AI in the UK: the stack inside the coastline, and the four
 * things that changes. A band on its page's rail: the home's since
 * 2026-10-01, in the place of the infrastructure band's one line on
 * Project Venus, and under a UK hero in the playground.
 */
export function UkStory({ look = "editorial" }: { look?: Look }) {
  const app = look === "app";
  const rule = app ? "border-[hsl(var(--border))]" : "border-[var(--rule)]";
  return (
    <section id="uk" className={`scroll-mt-24 border-b py-20 ${app ? rule : "border-dotted border-[var(--rule)]"}`}>
      <div className="grid items-center gap-14 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)]">
        <div>
          <p
            className={
              app ? "text-[11px] font-medium tracking-[0.06em] text-[hsl(var(--primary))] uppercase" : `${DATA_LABEL_CLS} text-[var(--theme-accent-text)]`
            }
          >
            Why keep AI in the UK
          </p>
          <h2 style={DISPLAY} className={`${INK_88} mt-4 max-w-[15ch] text-[clamp(2.2rem,4vw,3.4rem)] leading-[1.02] font-semibold tracking-[-0.035em]`}>
            Your question never leaves the UK.
          </h2>
          <p className={`mt-5 max-w-[48ch] ${INK_72} text-[1.05rem] leading-[1.65]`}>
            For many firms the hard question about AI isn’t what it can do. It’s where the data goes. A fully UK-hosted Twin keeps the whole path, from
            the question to the answer, inside the UK.
          </p>
          <ol className={`mt-10 list-none border-t p-0 ${rule}`}>
            {BENEFITS.map((benefit, index) => (
              <li key={benefit.title} className={`grid grid-cols-[2.5rem_minmax(0,1fr)] gap-2 border-b py-4 ${rule}`}>
                <span className="pt-0.5 text-[0.8rem] font-semibold text-[var(--theme-accent-text)] tabular-nums">{String(index + 1).padStart(2, "0")}</span>
                <span>
                  <span style={DISPLAY} className={`block text-[1.05rem] leading-[1.3] font-semibold ${INK_88}`}>
                    {benefit.title}
                  </span>
                  <span className={`mt-1 block text-[0.93rem] leading-[1.55] ${INK_72}`}>{benefit.body}</span>
                </span>
              </li>
            ))}
          </ol>
          <p className={`mt-6 text-[0.88rem] leading-[1.6] ${INK_72}`}>
            Runs on Project Venus, our private model: a draft model that predicts tokens ahead for speed, an adapter trained on real tasks, and no
            metering on token usage, subject to concurrency thresholds.{" "}
            <a href="/research#uk" className="font-medium whitespace-nowrap text-[var(--theme-accent-text)] underline underline-offset-4">
              Read the research
            </a>
          </p>
        </div>
        <BoundaryPanel />
      </div>
    </section>
  );
}
